import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  List,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { UnifiedEnrollmentResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import {
  useMyEnrollments,
  useCancelEnrollment,
} from "../hooks/useMyEnrollments";
import { EnrollmentRow } from "../components/EnrollmentRow";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Surface 5 — the player's own enrollments (/me/classes).
 *
 * Lists the token user's enrollments (server-scoped — GET /programs/enrollments/me).
 * Each row renders the status + fee paid from the response verbatim. Actions:
 *   - Cancel — calls the seam, invalidates the list (the row reflects the new
 *     server state); a failure surfaces the backend error, never a fake cancel.
 *   - A PROMOTED waitlist enrollment exposes Claim + Decline via <ClaimSection>;
 *     a PAID promoted spot reveals the facility-scoped card UI and pays on claim.
 *
 * Honest loading / empty / error states throughout.
 */
export function MyClassesPage() {
  const { user } = useAuth();
  if (!user) return null;
  return <MyClassesBody key={user.uid} actorId={user.uid} />;
}

function MyClassesBody({ actorId }: { actorId: string }) {
  const query = useMyEnrollments();
  const queryClient = useQueryClient();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requested = params.getAll("enrollment_id");
  const hasSelection = requested.length > 0;
  const selectedId = requested.length === 1 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requested[0] ?? "")
    ? requested[0]?.toLowerCase() : null;
  params.delete("enrollment_id");
  const remainingSearch = params.toString();
  const allClasses = `${location.pathname}${remainingSearch ? `?${remainingSearch}` : ""}${location.hash}`;
  const sourceIsCurrent = query.isSuccess && query.isFetchedAfterMount &&
    query.fetchStatus === "idle" && query.error === null;
  const [hasAuthorizedRows, setHasAuthorizedRows] = useState(false);
  useEffect(() => {
    if (sourceIsCurrent) setHasAuthorizedRows(true);
  }, [sourceIsCurrent]);
  const data = hasSelection
    ? query.data?.filter((row) => row.enrollment_id.toLowerCase() === selectedId)
    : query.data;
  const cancel = useCancelEnrollment();
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // The enrollment whose cancel is pending confirmation (null = none).
  const [confirming, setConfirming] =
    useState<UnifiedEnrollmentResponse | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function canAct(enrollment: UnifiedEnrollmentResponse) {
    const live = queryClient.getQueryState<UnifiedEnrollmentResponse[]>([
      ...queryKeys.classes.myEnrollments(), actorId,
    ]);
    return sourceIsCurrent && live?.status === "success" &&
      live.fetchStatus === "idle" && !live.isInvalidated &&
      live.data?.some((row) => row.enrollment_id === enrollment.enrollment_id &&
        row.status === enrollment.status) === true;
  }

  function runCancel(enrollment: UnifiedEnrollmentResponse) {
    if (!canAct(enrollment) || pending.current ||
        (enrollment.status !== "enrolled" && enrollment.status !== "waitlisted")) return;
    pending.current = true;
    setActionError(null);
    cancel.mutate(enrollment.enrollment_id, {
      onSuccess: () => { if (mounted.current) setConfirming(null); },
      onError: (e) => {
        if (mounted.current) setActionError(
          isApiError(e) ? e.message : "Couldn't cancel. Please try again.",
        );
      },
      onSettled: () => { pending.current = false; },
    });
  }

  let body: React.ReactNode;
  if (query.fetchStatus === "paused") {
    body = <Alert severity="info" role="status">Waiting for a connection to refresh your classes.</Alert>;
  } else if (query.isError) {
    body = (
      <Alert severity="error">
        Something went wrong loading your classes. Please try again.
        <Button color="inherit" onClick={() => void query.refetch()}>Retry</Button>
      </Alert>
    );
  } else if (!sourceIsCurrent) {
    body = (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        sx={{ alignItems: "center" }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading your classes…</Typography>
      </Stack>
    );
  } else if (hasSelection && (!data || data.length === 0)) {
    body = <Alert severity="info">This enrollment is unavailable in your account.</Alert>;
  } else if (!data || data.length === 0) {
    body = (
      <Typography color="text.secondary" role="status">
        You haven&apos;t enrolled in any classes yet. Browse classes to join.
      </Typography>
    );
  } else body = null;

  return (
    <Page title="My classes">
      {hasSelection ? (
        <Button component={RouterLink} to={allClasses} sx={{ mb: 2 }}>All my classes</Button>
      ) : null}
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      ) : null}
      {body}
      {(sourceIsCurrent || hasAuthorizedRows) && data && data.length > 0 ? (
        <List
          hidden={!sourceIsCurrent}
          inert={!sourceIsCurrent}
          disablePadding
          sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
        >
          {data.map((enrollment) => (
            <EnrollmentRow
              key={enrollment.enrollment_id}
              enrollment={enrollment}
              canAct={() => canAct(enrollment)}
              confirmingCancel={confirming?.enrollment_id === enrollment.enrollment_id}
              onRequestCancel={() => { if (canAct(enrollment)) setConfirming(enrollment); }}
              onConfirmCancel={() => runCancel(enrollment)}
              busy={cancel.isPending}
            />
          ))}
        </List>
      ) : null}
    </Page>
  );
}
